/**
 * "Pixel's Memory Core" — the site-wide quest.
 *
 * Pixel's memory core shattered when he crash-landed; five shards are hidden across the site, each
 * behind (or inside) something that's already there. Pixel stays in the chat corner the whole time
 * and gives clues. Steps, in order:
 *  button     behind the hero's "Contact us" button → drag the button away
 *  service    inside the "Virtual Worlds" service → open it
 *  logo       behind the logo, top-left of the very first screen → drag it away (concealed)
 *  project    behind the Unique Jewelry project picture → drag it away
 *  footer     behind the footer headline → drag it away
 *  restore    all five found → he flies into the middle of the footer by himself
 *  finale     there: timing game (memory restored), he tells you what he remembers, you charge his
 *             battery for take-off, and he launches home
 */
import type { FaceGlyph } from './faceGlyph';
import type { CheerKind } from './questCheer';

export type QuestStep = 'button' | 'service' | 'logo' | 'project' | 'footer' | 'restore' | 'finale';
export type QuestStatus = 'idle' | 'active' | 'done' | 'quit';
/**
 * Restoration staging — all in the middle of the footer, pinned to the page: talk (he talks to you
 * from there), celebrate (a quick happy hop), qte (timing game), charge (his battery screen —
 * click to charge him for take-off); then rise (lift-off, straight from that spot)
 */
export type FinaleStage = 'talk' | 'celebrate' | 'qte' | 'charge' | 'rise';

export type QuestEvent =
  | 'none'
  | 'start'
  | 'found-button'
  | 'found-service'
  | 'found-logo'
  | 'found-project'
  | 'found-footer'
  | 'restore'
  | 'charged'
  | 'complete'
  | 'jump'
  | 'quit';

/** Restoration beats you can jump straight to (testing) */
export type FinaleJump = 'qte' | 'story' | 'charge' | 'launch';

export interface QuestState {
  status: QuestStatus;
  step: QuestStep;
  /** Shards found so far (0..SHARD_COUNT) */
  shards: number;
  /** Bumps on every change, so listeners can react to repeats */
  tick: number;
  finaleStage: FinaleStage;
  event: QuestEvent;
}

export const QUEST_STEPS: QuestStep[] = ['button', 'service', 'logo', 'project', 'footer', 'restore', 'finale'];
export const SHARD_COUNT = 5;

/** Which `data-quest-spot` hides the shard of each drag-to-uncover step */
const SPOT_OF: Partial<Record<QuestStep, string>> = {
  button: 'button',
  logo: 'logo',
  project: 'project-3',
  footer: 'footer',
};

class QuestStore {
  private state: QuestState = {
    status: 'idle',
    step: 'button',
    shards: 0,
    tick: 0,
    event: 'none',
    finaleStage: 'talk',
  };
  /** The timing-game / reboot face, mutated every frame by the restoration */
  qteGlyph: FaceGlyph = { icon: 'slider', text: '', value: 0.5, zone: 0.2 };
  /** Finished the quest: he flew home. Pixel is gone from the site until the page is reloaded */
  private departed = false;
  hasDeparted = () => this.departed;
  setFinaleStage = (finaleStage: FinaleStage) => {
    if (this.state.status === 'active') this.set({ finaleStage });
  };
  private used = false;
  /** His face glitches from the moment you ask why, until the quest is finished */
  private glitching = false;
  isGlitching = () => this.glitching;
  startGlitching = () => {
    if (this.state.status !== 'done') this.glitching = true;
  };
  /** Memory restored (timing game won): no more glitches, for the rest of the finale */
  stopGlitching = () => {
    this.glitching = false;
  };
  private listeners = new Set<() => void>();
  /** Pixel's live centre + radius on screen (viewport px), written by the 3D scene every frame */
  pixelPx = { x: -9999, y: -9999, r: 0 };
  /** Restoration: presses/releases on the page go here instead of Pixel's usual reactions */
  finaleInput: ((down: boolean, onPixel: boolean) => void) | null = null;
  /** What his face shows while he's out of the corner (written by the pilot, read by the 3D scene) */
  faceGlyph: FaceGlyph | null = null;
  /** Restoration beat requested by the last jump (read by QuestFinale on the 'jump' event) */
  finaleJump: FinaleJump | null = null;
  /** The celebration playing right now (read by the 3D scene every frame) */
  cheerNow: { kind: CheerKind; at: number } | null = null;
  /** Start a celebration (performance-clock ms) */
  cheer = (kind: CheerKind) => {
    this.cheerNow = { kind, at: performance.now() };
  };
  /** Take-off charge level 0..1, written by the 3D scene every frame (drives the page rumble) */
  batteryLevel = 0;
  /** Take-off charge: is his battery screen up? (read by the 3D scene every frame) */
  wantsBattery = () => this.state.status === 'active' && this.state.step === 'finale' && this.state.finaleStage === 'charge';
  /** The 3D scene reports a full battery → ready for take-off */
  batteryFull = () => {
    if (this.wantsBattery()) this.set({ event: 'charged' });
  };
  /** Where he is on screen (fractions of the viewport) the moment he lifts off */
  liftFrom = { x: 0.5, y: 0.5 };
  /** Remember where he is right now, so the lift-off starts exactly there */
  markLiftOff = () => {
    const p = this.pixelPx;
    this.liftFrom =
      p.x > -9000
        ? { x: Math.min(0.9, Math.max(0.1, p.x / window.innerWidth)), y: p.y / window.innerHeight }
        : { x: 0.5, y: 0.6 };
  };

  /** Pixel himself is the only control while his memory is being restored */
  takesPixelInput = () => this.state.status === 'active' && (this.state.step === 'restore' || this.state.step === 'finale');

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  getState = () => this.state;
  isUsed = () => this.used;
  isActive = () => this.state.status === 'active';

  private set(patch: Partial<QuestState>) {
    // Events are one-shot: a change that doesn't name one carries 'none', so the last one never replays
    this.state = { ...this.state, event: 'none', ...patch, tick: this.state.tick + 1 };
    this.listeners.forEach((l) => l());
  }

  /** The quest can be played as often as you like — nothing is ever used up */
  private markUsed() {}

  start = () => {
    if (this.state.status === 'active') return;
    this.glitching = true;
    this.set({ status: 'active', step: 'button', shards: 0, event: 'start', finaleStage: 'talk' });
  };

  /** Is a shard hiding behind this spot right now? (read by the elements that hide shards) */
  spotLive = (spot: string) => this.state.status === 'active' && SPOT_OF[this.state.step] === spot;

  /** A shard was picked up at the given step (ignored unless it's the current one) */
  found = (step: QuestStep) => {
    const s = this.state;
    if (s.status !== 'active' || s.step !== step) return;
    const next = QUEST_STEPS[QUEST_STEPS.indexOf(step) + 1];
    this.set({ step: next, shards: s.shards + 1, event: `found-${step}` as QuestEvent });
  };

  /** Picked up the shard hidden behind a spot */
  collect = (spot: string) => {
    if (this.spotLive(spot)) this.found(this.state.step);
  };

  /** All five found: he flies straight into the middle of the footer */
  restore = () => {
    if (this.state.status !== 'active' || this.state.step !== 'restore') return;
    this.set({ step: 'finale', finaleStage: 'talk', event: 'restore' });
  };

  complete = () => {
    if (this.state.status !== 'active' || this.state.step !== 'finale') return;
    this.markUsed();
    this.glitching = false; // memory restored: no more glitches
    this.departed = true; // …and he's flown home
    this.set({ status: 'done', event: 'complete' });
  };

  /**
   * Testing: drop straight into any point of the quest, as if everything before it was done.
   * Fires the event that normally leads into that step, so Pixel briefs it as usual.
   */
  jumpTo = (step: QuestStep, opts: { finale?: FinaleJump } = {}) => {
    this.glitching = true;
    this.departed = false;
    this.finaleJump = opts.finale ?? null;
    const idx = QUEST_STEPS.indexOf(step);
    const event: QuestEvent =
      step === 'button' ? 'start' : step === 'finale' ? 'jump' : (`found-${QUEST_STEPS[idx - 1]}` as QuestEvent);
    this.set({
      status: 'active',
      step,
      shards: Math.min(SHARD_COUNT, idx),
      event,
      finaleStage: step === 'finale' && opts.finale === 'qte' ? 'qte' : 'talk',
    });
  };

  quit = () => {
    if (this.state.status !== 'active') return;
    this.markUsed();
    this.set({ status: 'quit', event: 'quit' });
  };
}

export const quest = new QuestStore();
